import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Zap, Loader2, RefreshCw, Check, X, AlertTriangle, Clock, Target,
  ShieldAlert, Calculator, History
} from 'lucide-react';
import Navbar from '../components/Navbar';
import MacroPulse from '../components/MacroPulse';
import Footer from '../components/Footer';
import ProPaywall from '../components/ProPaywall';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => n === null || n === undefined ? '—'
  : n >= 1000 ? n.toLocaleString('en-US', { maximumFractionDigits: 1 })
  : n.toLocaleString('en-US', { maximumFractionDigits: 4 });

const scoreColor = (s) => s >= 8 ? '#10B981' : s >= 5 ? '#C5A059' : '#EF4444';

const ScoreBar = ({ label, score }) => (
  <div className="flex-1" data-testid={`score-${label.toLowerCase()}`}>
    <div className="flex items-center justify-between mb-1">
      <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500">{label}</span>
      <span className="text-sm font-bold" style={{ color: scoreColor(score) }}>{score}/10</span>
    </div>
    <div className="h-2 bg-white/10 rounded-full overflow-hidden">
      <div className="h-full rounded-full transition-all" style={{ width: `${score * 10}%`, background: scoreColor(score) }} />
    </div>
  </div>
);

const PositionCalc = ({ signal }) => {
  const [account, setAccount] = useState('10000');
  const [riskPct, setRiskPct] = useState('1');
  const acct = parseFloat(account) || 0;
  const pct = parseFloat(riskPct) || 0;
  const riskUsd = acct * pct / 100;
  const units = signal.risk_per_unit > 0 ? riskUsd / signal.risk_per_unit : 0;

  return (
    <div className="mt-3 bg-black/40 border border-white/10 rounded-lg p-3" data-testid="position-calculator">
      <p className="text-[11px] text-gray-500 font-semibold mb-2 inline-flex items-center gap-1">
        <Calculator size={12} className="text-[#C5A059]" /> POSITION SIZE CALCULATOR
      </p>
      <div className="grid grid-cols-2 gap-2 mb-2">
        <div>
          <label className="text-[10px] text-gray-600 block mb-1">Account size ($)</label>
          <Input value={account} onChange={e => setAccount(e.target.value)} type="number"
            className="bg-black/50 border-white/10 text-white h-8 text-xs" data-testid="calc-account-input" />
        </div>
        <div>
          <label className="text-[10px] text-gray-600 block mb-1">Risk %</label>
          <Input value={riskPct} onChange={e => setRiskPct(e.target.value)} type="number" step="0.1"
            className="bg-black/50 border-white/10 text-white h-8 text-xs" data-testid="calc-risk-input" />
        </div>
      </div>
      <div className="flex justify-between text-xs">
        <span className="text-gray-500">Position: <span className="text-white font-mono" data-testid="calc-units">{fmt(units)} units</span></span>
        <span className="text-gray-500">Risk: <span className="text-red-400 font-mono" data-testid="calc-risk-usd">${fmt(riskUsd)}</span></span>
      </div>
    </div>
  );
};

const AssetCard = ({ asset }) => {
  if (asset.error) {
    return (
      <div className="bg-[#111111] border border-white/10 rounded-xl p-5" data-testid={`signal-card-${asset.symbol.replace(/[^A-Za-z0-9]/g, '')}`}>
        <h3 className="text-white font-semibold">{asset.symbol}</h3>
        <p className="text-gray-600 text-sm mt-2">{asset.error}</p>
      </div>
    );
  }
  const hot = !!asset.signal;
  return (
    <div className={`bg-[#111111] border rounded-xl p-5 transition-all ${hot ? 'border-emerald-500/60 shadow-[0_0_25px_rgba(16,185,129,0.15)]' : 'border-white/10'}`}
      data-testid={`signal-card-${asset.symbol.replace(/[^A-Za-z0-9]/g, '')}`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-white font-manrope font-bold text-lg">{asset.symbol}</h3>
          <p className="text-[#C5A059] font-mono text-sm">${fmt(asset.price)}</p>
        </div>
        {hot && (
          <span className="inline-flex items-center gap-1 bg-emerald-500/15 border border-emerald-500/40 text-emerald-400 text-[11px] font-bold px-3 py-1 rounded-full animate-pulse" data-testid="hot-signal-badge">
            <Zap size={12} /> {asset.signal.direction} {asset.signal.score}/10
          </span>
        )}
      </div>

      <div className="flex gap-4 mb-4">
        <ScoreBar label="Long" score={asset.long_score} />
        <ScoreBar label="Short" score={asset.short_score} />
      </div>

      {asset.macro_warning && (
        <div className="flex items-start gap-2 bg-red-500/5 border border-red-500/25 rounded-lg px-3 py-2 mb-3" data-testid="macro-headwind-banner">
          <AlertTriangle size={13} className="text-red-400 mt-0.5 shrink-0" />
          <p className="text-red-300/80 text-xs">{asset.macro_warning}</p>
        </div>
      )}
      {asset.macro_boost && (
        <div className="flex items-start gap-2 bg-emerald-500/5 border border-emerald-500/25 rounded-lg px-3 py-2 mb-3" data-testid="macro-tailwind-banner">
          <Zap size={13} className="text-emerald-400 mt-0.5 shrink-0" />
          <p className="text-emerald-300/80 text-xs">{asset.macro_boost}</p>
        </div>
      )}

      {/* Glass-box checklist */}
      <div className="border border-white/5 rounded-lg overflow-hidden mb-1" data-testid="condition-checklist">
        <div className="grid grid-cols-[1fr_44px_44px] text-[10px] font-bold uppercase text-gray-600 bg-white/[0.03] px-3 py-1.5">
          <span>Condition</span><span className="text-center">Long</span><span className="text-center">Short</span>
        </div>
        {asset.checks.map(c => (
          <div key={c.key} className="grid grid-cols-[1fr_44px_44px] items-center px-3 py-1.5 border-t border-white/5 text-xs">
            <div>
              <span className="text-gray-300">{c.label}</span>
              <span className="text-gray-600 ml-1.5 text-[10px]">+{c.points} · {c.detail}</span>
            </div>
            <span className="text-center">{c.long ? <Check size={14} className="text-emerald-500 inline" /> : <X size={14} className="text-gray-700 inline" />}</span>
            <span className="text-center">{c.short ? <Check size={14} className="text-red-400 inline" /> : <X size={14} className="text-gray-700 inline" />}</span>
          </div>
        ))}
      </div>

      {/* Signal panel */}
      {hot && (
        <div className="mt-4 bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-3" data-testid="signal-panel">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div><p className="text-[10px] text-gray-500">ENTRY</p><p className="text-white font-mono text-sm">${fmt(asset.signal.entry)}</p></div>
            <div><p className="text-[10px] text-gray-500 inline-flex items-center gap-1"><ShieldAlert size={10} /> STOP</p><p className="text-red-400 font-mono text-sm">${fmt(asset.signal.stop)}</p></div>
            <div><p className="text-[10px] text-gray-500 inline-flex items-center gap-1"><Target size={10} /> TARGET (1:2)</p><p className="text-emerald-400 font-mono text-sm">${fmt(asset.signal.target)}</p></div>
          </div>
          <PositionCalc signal={asset.signal} />
        </div>
      )}
    </div>
  );
};

export default function SignalEngine() {
  const { isPro, getToken } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [recentSignals, setRecentSignals] = useState([]);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/signals/dashboard`, {
        headers: { Authorization: `Bearer ${getToken()}` } });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load signals');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isPro) return;
    fetchDashboard();
    axios.get(`${API}/signals/log?limit=8`).then(r => setRecentSignals(r.data.signals)).catch(() => {});
    const iv = setInterval(fetchDashboard, 120000);
    return () => clearInterval(iv);
  }, [isPro, fetchDashboard]);

  if (!isPro) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <Navbar />
        <div className="pt-32"><ProPaywall feature="the GC PRO Signal Engine" /></div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="signal-engine-page">
      <Navbar />
      <div className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#C5A059] to-[#D4AF37] flex items-center justify-center">
                <Zap size={20} className="text-black" />
              </div>
              <h1 className="font-manrope font-bold text-3xl sm:text-4xl text-white">
                GC PRO <span className="text-[#C5A059]">Signal Engine</span>
              </h1>
            </div>
            <p className="text-gray-400 text-sm max-w-2xl">
              Live confluence scoring on the 1H timeframe — seven institutional conditions per asset,
              fully transparent. The reasoning chain is the product.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {data && (
              <span className="text-xs text-gray-600 inline-flex items-center gap-1" data-testid="last-updated">
                <Clock size={12} /> {new Date(data.updated_at).toLocaleTimeString()}
              </span>
            )}
            <button onClick={fetchDashboard} disabled={loading}
              className="p-2 rounded-lg bg-[#111111] border border-white/10 text-gray-400 hover:text-[#C5A059] transition-colors"
              data-testid="refresh-signals-btn">
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        <div className="flex items-start gap-3 bg-amber-500/5 border border-amber-500/20 rounded-xl px-4 py-3 mb-6" data-testid="signals-disclaimer">
          <AlertTriangle size={16} className="text-amber-500 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-amber-200/70">
            Educational tool — not financial advice. Past signals do not guarantee future results.
            Scores measure technical confluence, not certainty.
          </p>
        </div>

        {error && <div className="bg-red-500/5 border border-red-500/20 rounded-xl px-4 py-3 text-red-400 text-sm mb-6" data-testid="signals-error">{error}</div>}

        <MacroPulse />

        {loading && !data ? (
          <div className="flex items-center justify-center py-24" data-testid="signals-loading">
            <Loader2 size={30} className="animate-spin text-[#C5A059]" />
            <span className="ml-3 text-gray-400">Scanning the watchlist...</span>
          </div>
        ) : data && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="signal-cards-grid">
            {data.assets.map(a => <AssetCard key={a.symbol} asset={a} />)}
          </div>
        )}

        {/* Public signal log */}
        <div className="mt-12" data-testid="signal-log-section">
          <div className="flex items-center gap-2 mb-4">
            <History size={18} className="text-[#C5A059]" />
            <h2 className="text-white font-manrope font-bold text-xl">Logged Signals (publicly verifiable)</h2>
          </div>
          {recentSignals.length === 0 ? (
            <p className="text-gray-600 text-sm bg-[#111111] border border-dashed border-white/10 rounded-lg px-4 py-5 text-center">
              No 8+/10 signals logged yet. Every high-confluence signal is recorded here with its full checklist.
            </p>
          ) : (
            <div className="space-y-2">
              {recentSignals.map(s => (
                <div key={s.signal_id} className="flex flex-wrap items-center gap-3 bg-[#111111] border border-white/10 rounded-lg px-4 py-3 text-xs">
                  <span className={`font-bold px-2 py-0.5 rounded-full ${s.direction === 'LONG' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>{s.direction}</span>
                  <span className="text-white font-semibold">{s.asset}</span>
                  <span className="text-[#C5A059] font-bold">{s.score}/10</span>
                  <span className="text-gray-500 font-mono">entry ${fmt(s.entry)} · stop ${fmt(s.stop)} · target ${fmt(s.target)}</span>
                  <span className="text-gray-600 ml-auto">{new Date(s.timestamp).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
